import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useClientPortalPaths } from "../../hooks/useClientPortalBase";
import {
  getAnalyticsSummary,
  getAnalyticsTrends,
  getAnalyticsPipeline,
  getAnalyticsSources,
  getAnalyticsCommunities,
  getAnalyticsCourses,
  getAnalyticsRecentActivity,
  exportReportCsv,
} from "../../api/clientAnalyticsApi";
import { getClientPortalView } from "../../api/clientPortalApi";
import ClientSummaryMetrics from "../../components/clientPortal/ClientSummaryMetrics";
import ClientAnalyticsDateRange from "../../components/clientPortal/ClientAnalyticsDateRange";
import ClientSimpleTrendChart from "../../components/clientPortal/ClientSimpleTrendChart";
import ClientSimpleBarChart from "../../components/clientPortal/ClientSimpleBarChart";
import {
  ClientPortalEmpty,
  ClientPortalError,
  ClientPortalLoading,
} from "../../components/clientPortal/ClientPortalStates";
import { buildAnalyticsQuery, formatHours, formatPercent } from "../../utils/clientAnalyticsQuery";
import { formatClientDate, formatClientDateTime } from "../../utils/clientPortalFormat";

const ClientAnalyticsPage = () => {
  const { clientViewId } = useParams();
  const [viewName, setViewName] = useState("");
  const [preset, setPreset] = useState("30d");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [exporting, setExporting] = useState(false);
  const paths = useClientPortalPaths();

  const query = buildAnalyticsQuery({ preset, dateFrom, dateTo });

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const [viewBody, summary, trends, pipeline, sources, communities, courses, activity] =
        await Promise.all([
          getClientPortalView(clientViewId),
          getAnalyticsSummary(clientViewId, query),
          getAnalyticsTrends(clientViewId, query),
          getAnalyticsPipeline(clientViewId, query),
          getAnalyticsSources(clientViewId, query),
          getAnalyticsCommunities(clientViewId, query),
          getAnalyticsCourses(clientViewId, query),
          getAnalyticsRecentActivity(clientViewId, { limit: 10 }),
        ]);
      setViewName(viewBody.clientView?.name || "Client View");
      setData({
        summary,
        trends,
        pipeline,
        sources,
        communities,
        courses,
        activity,
      });
    } catch (error) {
      setErrorMessage(error.message || "We could not load analytics. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [clientViewId, preset, dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDateChange = ({ preset: nextPreset, dateFrom: from, dateTo: to }) => {
    if (nextPreset != null) setPreset(nextPreset);
    if (from != null) setDateFrom(from);
    if (to != null) setDateTo(to);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportReportCsv(clientViewId, { ...query, reportType: "opportunities" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "opportunities-report.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setErrorMessage(error.message || "Export failed.");
    } finally {
      setExporting(false);
    }
  };

  const summary = data?.summary?.summary;
  const kpiMetrics = summary
    ? [
        { label: "Total Opportunities", value: summary.totalOpportunities },
        { label: "New", value: summary.newOpportunities },
        { label: "Contacted", value: summary.contactedOpportunities },
        { label: "Closed / Won", value: summary.closedWonOpportunities },
        { label: "Conversion Rate", value: formatPercent(summary.conversionRate) },
        { label: "Needs Follow-Up", value: summary.needsFollowUpCount },
        {
          label: "Est. Opportunity Value",
          value:
            summary.estimatedTotalValue != null
              ? `$${Number(summary.estimatedTotalValue).toLocaleString()}`
              : "—",
        },
        { label: "Avg. Time to Contact", value: formatHours(summary.averageTimeToContactHours) },
      ]
    : [];

  const pipelineChart = (data?.pipeline?.pipeline || []).map((p) => ({
    label: p.statusLabel,
    value: p.count,
  }));

  const sourceChart = (data?.sources?.sources || []).slice(0, 8).map((s) => ({
    label: s.sourceName,
    value: s.opportunityCount,
  }));

  const hasOpportunities = (summary?.totalOpportunities ?? 0) > 0;

  return (
    <>
      <section className="panel-card">
        <p className="eyebrow">
          <Link to={paths.view(clientViewId)}>{viewName}</Link> / Analytics
        </p>
        <div className="client-portal-section-header">
          <div>
            <h2>Analytics & Lead Reporting</h2>
            <p className="muted">Understand leads and performance for your Client View</p>
          </div>
          <div className="client-view-card__actions">
            <Link to={paths.viewSection(clientViewId, "reports")} className="cv-button-link">
              All Reports
            </Link>
            <button
              type="button"
              className="midpoint-share-button secondary"
              disabled={exporting}
              onClick={handleExport}
            >
              {exporting ? "Exporting…" : "Export CSV"}
            </button>
          </div>
        </div>
        <ClientAnalyticsDateRange
          preset={preset}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onChange={handleDateChange}
        />
        {data?.summary?.refreshedAt ? (
          <p className="muted">Last refreshed {formatClientDateTime(data.summary.refreshedAt)}</p>
        ) : null}
      </section>

      {loading ? <ClientPortalLoading message="Loading analytics…" /> : null}
      {errorMessage ? <ClientPortalError message={errorMessage} /> : null}

      {!loading && data && !hasOpportunities ? (
        <ClientPortalEmpty
          title="No opportunities in this period"
          message="No opportunities were generated during this date range."
        />
      ) : null}

      {!loading && data && hasOpportunities ? (
        <>
          <ClientSummaryMetrics metrics={kpiMetrics} />

          <section className="panel-card">
            <h3>Lead trend</h3>
            <ClientSimpleTrendChart trends={data.trends?.trends || []} />
          </section>

          <div className="client-analytics-grid">
            <section className="panel-card">
              <h3>Pipeline status</h3>
              <ClientSimpleBarChart data={pipelineChart} labelKey="label" valueKey="value" />
            </section>
            <section className="panel-card">
              <h3>Lead sources</h3>
              <ClientSimpleBarChart data={sourceChart} labelKey="label" valueKey="value" />
            </section>
          </div>

          <section className="panel-card">
            <div className="client-portal-section-header">
              <h3>Top communities</h3>
              <Link
                to={paths.viewSection(clientViewId, "reports", "communities")}
                className="cv-button-link"
              >
                Full report
              </Link>
            </div>
            <ReportTable
              headers={["Community", "Leads", "New", "Won", "Conversion", "Last lead"]}
              rows={(data.communities?.communities || []).slice(0, 5).map((c) => [
                c.communityName,
                c.opportunityCount,
                c.newOpportunities,
                c.closedWonOpportunities,
                formatPercent(c.conversionRate),
                c.lastOpportunityDate ? formatClientDate(c.lastOpportunityDate) : "—",
              ])}
            />
          </section>

          <section className="panel-card">
            <div className="client-portal-section-header">
              <h3>Top courses</h3>
              <Link to={paths.viewSection(clientViewId, "reports", "courses")} className="cv-button-link">
                Full report
              </Link>
            </div>
            <ReportTable
              headers={["Course", "Community", "Leads", "Won", "Last lead"]}
              rows={(data.courses?.courses || []).slice(0, 5).map((c) => [
                c.courseName,
                c.communityName || "—",
                c.opportunityCount,
                c.closedWonOpportunities,
                c.lastOpportunityDate ? formatClientDate(c.lastOpportunityDate) : "—",
              ])}
            />
          </section>

          <section className="panel-card">
            <h3>Recent lead activity</h3>
            <ul className="client-analytics-activity">
              {(data.activity?.activity || []).map((item) => (
                <li key={`${item.activityKind}-${item.opportunityId}-${item.occurredAt}`}>
                  <Link to={paths.viewSection(clientViewId, "opportunities", item.opportunityId)}>
                    {activityLabel(item)}
                  </Link>
                  <span className="muted"> · {formatClientDateTime(item.occurredAt)}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="panel-card client-sponsor-placeholder">
            <h3>Sponsor & Campaign Performance</h3>
            <p className="muted">Coming soon — sponsor and campaign metrics will appear here.</p>
            <div className="cv-studio-metrics-grid">
              <div className="panel-card muted">Featured community impressions — Coming soon</div>
              <div className="panel-card muted">Featured course clicks — Coming soon</div>
              <div className="panel-card muted">Sponsor leads — Coming soon</div>
              <div className="panel-card muted">Campaign conversion — Coming soon</div>
            </div>
          </section>
        </>
      ) : null}
    </>
  );
};

function activityLabel(item) {
  switch (item.activityKind) {
    case "opportunity_created":
      return `New opportunity: ${item.title || item.opportunityNumber}`;
    case "status_changed":
      return `Status updated: ${item.title || item.opportunityNumber}`;
    case "follow_up_changed":
      return `Follow-up updated: ${item.title || item.opportunityNumber}`;
    default:
      return item.subject || `Activity on ${item.title || "opportunity"}`;
  }
}

function ReportTable({ headers, rows }) {
  if (!rows.length) return <p className="muted">No rows to display.</p>;
  return (
    <table className="admin-table">
      <thead>
        <tr>
          {headers.map((h) => (
            <th key={h}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            {row.map((cell, j) => (
              <td key={j}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default ClientAnalyticsPage;
